import {
  complimentaryEnhancedFreeFlagPayload,
  isEldoradoSiteSignup,
} from "./eldoradoSignup";
import { encryptPayload } from "./utils";

export const AFFILIATE_USER_STORAGE_KEY = "is_affiliate_user";
export const AFFILIATE_FROM_WHERE = "affiliate_site";

const isTruthyAffiliateFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

/** True when from_where identifies an affiliate-site signup / resume user. */
export const isAffiliateFromWhere = (source) => {
  if (source === undefined || source === null) {
    if (typeof window === "undefined") {
      return false;
    }
    return localStorage.getItem("from_where") === AFFILIATE_FROM_WHERE;
  }
  if (typeof source === "string") {
    return source === AFFILIATE_FROM_WHERE;
  }
  return (
    source?.from_where === AFFILIATE_FROM_WHERE ||
    source?.user?.from_where === AFFILIATE_FROM_WHERE ||
    source?.data?.from_where === AFFILIATE_FROM_WHERE ||
    source?.data?.user?.from_where === AFFILIATE_FROM_WHERE
  );
};

/** True when localStorage or a user/response object marks affiliate complimentary. */
export const isAffiliateUser = (source) => {
  if (isAffiliateFromWhere(source)) {
    return true;
  }
  if (source === undefined || source === null) {
    if (typeof window === "undefined") {
      return false;
    }
    return isTruthyAffiliateFlag(
      localStorage.getItem(AFFILIATE_USER_STORAGE_KEY),
    );
  }
  if (typeof source === "boolean") {
    return source;
  }
  if (typeof source === "string" || typeof source === "number") {
    return isTruthyAffiliateFlag(source);
  }

  return (
    isTruthyAffiliateFlag(source?.is_affiliate_user) ||
    isTruthyAffiliateFlag(source?.user?.is_affiliate_user) ||
    isTruthyAffiliateFlag(source?.data?.is_affiliate_user) ||
    isTruthyAffiliateFlag(source?.data?.user?.is_affiliate_user)
  );
};

export const COUNTY_FREE_ENHANCED_KEY = "county_free_enhanced";

/** County page stored Offer Enhanced Monitoring at No Cost for this visit. */
export const isCountyFreeEnhancedSignup = () =>
  typeof window !== "undefined" &&
  localStorage.getItem(COUNTY_FREE_ENHANCED_KEY) === "1";

/** Eldorado site signup, affiliate, or a county offering free Enhanced. */
export const isComplimentaryEnhancedSignup = (source) =>
  isEldoradoSiteSignup() ||
  isAffiliateUser(source) ||
  isCountyFreeEnhancedSignup();

const isTruthyComplimentaryFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

/** True when a user/response object (or localStorage) has is_complimentary. */
export const hasComplimentaryEnhancedFlag = (source) => {
  if (source === undefined || source === null) {
    if (typeof window === "undefined") {
      return false;
    }
    return isTruthyComplimentaryFlag(localStorage.getItem("is_complimentary"));
  }
  if (typeof source === "boolean" || typeof source === "number") {
    return isTruthyComplimentaryFlag(source);
  }
  if (typeof source === "string") {
    return isTruthyComplimentaryFlag(source);
  }
  return (
    isTruthyComplimentaryFlag(source?.is_complimentary) ||
    isTruthyComplimentaryFlag(source?.user?.is_complimentary) ||
    isTruthyComplimentaryFlag(source?.data?.is_complimentary) ||
    isTruthyComplimentaryFlag(source?.data?.user?.is_complimentary)
  );
};

/** Survives handoff clear so /property-monitoring-activated can show free copy. */
export const COMPLIMENTARY_MONITORING_ACTIVATED_KEY =
  "complimentary_monitoring_activated";

export const PROPERTY_MONITORING_ACTIVATED_PATH =
  "/property-monitoring-activated";

export const markComplimentaryMonitoringActivated = () => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(COMPLIMENTARY_MONITORING_ACTIVATED_KEY, "1");
};

export const isComplimentaryMonitoringActivated = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const value = localStorage.getItem(COMPLIMENTARY_MONITORING_ACTIVATED_KEY);
  return value === "1" || value === "true";
};

/**
 * Clears page-scoped complimentary activated flag only.
 * Does not clear account-level is_complimentary.
 */
export const clearComplimentaryMonitoringActivated = () => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(COMPLIMENTARY_MONITORING_ACTIVATED_KEY);
};

export const getPropertyMonitoringActivatedPath = ({
  complimentary = false,
} = {}) =>
  complimentary
    ? `${PROPERTY_MONITORING_ACTIVATED_PATH}?complimentary=true`
    : PROPERTY_MONITORING_ACTIVATED_PATH;

const isTruthyComplimentaryQueryValue = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

/** Persist complimentary activated flag from `?complimentary=true`. */
export const persistComplimentaryActivatedFromQuery = (search) => {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(
    typeof search === "string" ? search : window.location.search || "",
  );
  if (!isTruthyComplimentaryQueryValue(params.get("complimentary"))) {
    return false;
  }
  markComplimentaryMonitoringActivated();
  return true;
};

/** Title source of truth: query param and/or page-scoped localStorage flag. */
export const shouldShowComplimentaryActivatedTitle = (search) => {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(
    typeof search === "string" ? search : window.location.search || "",
  );
  if (isTruthyComplimentaryQueryValue(params.get("complimentary"))) {
    return true;
  }
  return isComplimentaryMonitoringActivated();
};

/**
 * Enhanced (Complimentary) for activated-page copy: El Dorado, affiliate,
 * county-investor free conversion, or any user with is_complimentary enabled.
 */
export const isComplimentaryEnhancedMonitoringContext = ({
  category,
  countyUserId,
  userLike,
} = {}) => {
  const resolvedCategory =
    category ||
    (typeof window !== "undefined"
      ? localStorage.getItem("signup_product_tier")
      : null);
  if (resolvedCategory && resolvedCategory !== "enhanced_monitoring") {
    return false;
  }

  if (isComplimentaryMonitoringActivated()) {
    return true;
  }
  if (isComplimentaryEnhancedSignup(userLike)) {
    return true;
  }
  if (
    hasComplimentaryEnhancedFlag(userLike) ||
    hasComplimentaryEnhancedFlag()
  ) {
    return true;
  }

  const resolvedCountyUserId =
    countyUserId !== undefined
      ? countyUserId
      : typeof window !== "undefined"
        ? localStorage.getItem("county_user_id")
        : null;
  return Boolean(resolvedCountyUserId);
};

/**
 * Mark activated-page complimentary copy when Enhanced (Complimentary) applies.
 * @returns {boolean} true when marked
 */
export const markComplimentaryMonitoringActivatedIfNeeded = (options = {}) => {
  if (!isComplimentaryEnhancedMonitoringContext(options)) {
    return false;
  }
  markComplimentaryMonitoringActivated();
  return true;
};

/**
 * Identify complimentary Enhanced, mark storage, and return the activated path.
 */
export const resolvePropertyMonitoringActivatedRedirect = (options = {}) => {
  const complimentary = markComplimentaryMonitoringActivatedIfNeeded(options);
  if (!complimentary) {
    clearComplimentaryMonitoringActivated();
  }
  return getPropertyMonitoringActivatedPath({ complimentary });
};

const hasCountyUserId = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const value = localStorage.getItem("county_user_id");
  return Boolean(value) && value !== "null" && value !== "undefined";
};

/**
 * Free-enhanced payment flags for customer/user signup (not admin).
 * Affiliate → is_affiliate_user;
 * county investor (not Eldorado) → county_investor_free_enhanced;
 * Eldorado → csi_free_enhanced.
 */
export const complimentaryEnhancedSignupPaymentFlagPayload = () =>
  complimentaryEnhancedFreeFlagPayload(false, {
    isAffiliate: isAffiliateUser(),
    isCountyInvestor: hasCountyUserId() && !isEldoradoSiteSignup(),
  });

export const clearAffiliateUserFlag = () => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(AFFILIATE_USER_STORAGE_KEY);
};

/**
 * On successful send-otp, persist affiliate complimentary Enhanced signup state.
 * Returns true when the affiliate flag was found and stored.
 */
export const persistAffiliateUserFromSendOtp = (response = {}) => {
  if (typeof window === "undefined") {
    return false;
  }
  if (!isAffiliateUser(response) && !isAffiliateUser(response?.data)) {
    return false;
  }

  localStorage.setItem(AFFILIATE_USER_STORAGE_KEY, "1");
  localStorage.setItem("category", encryptPayload("enhanced_monitoring"));
  localStorage.setItem("signup_product_tier", "enhanced_monitoring");
  localStorage.setItem("plain_signup", "true");
  return true;
};

/** Seed affiliate flag from admin resume / user row when present. */
export const persistAffiliateUserFromResume = (userLike = {}) => {
  if (typeof window === "undefined") {
    return false;
  }
  if (!isAffiliateUser(userLike)) {
    return false;
  }

  localStorage.setItem(AFFILIATE_USER_STORAGE_KEY, "1");
  localStorage.setItem("category", encryptPayload("enhanced_monitoring"));
  localStorage.setItem("signup_product_tier", "enhanced_monitoring");
  localStorage.setItem("plain_signup", "true");
  return true;
};
